import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";
import { toCanvas } from "html-to-image";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import {
  fetchData,
  getPendingRequestCount,
  waitForRequestsIdle,
} from "../../../utils/functions";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";
// import GeneralTab from "./tabs/GeneralTab";
import InformationMembersTab from "./tabs/InformationMembersTab";
import EducationTab from "./tabs/EducationTab";
import EmploymentTab from "./tabs/EmploymentTab";
import HousingTab from "./tabs/HousingTab";
import DrainageTab from "./tabs/DrainageTab";
import AssetsTab from "./tabs/AssetsTab";
import OthersTab from "./tabs/OthersTab";
import HealthStatusTab from "./tabs/HealthStatusTab";

const HouseHoldDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [activeSubTab, setActiveSubTab] = useState("info-members");
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState("");
  const tabContentRef = useRef(null);
  const leftPageRef = useRef(false);

  useEffect(() => {
    leftPageRef.current = false;
    return () => {
      leftPageRef.current = true;
    };
  }, []);
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    survey_type: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });

  const surveyTypeExtraFilter = [
    {
      key: "survey_type",
      label: "Survey Type",
      options: [
        { value: "Baseline", label: "Baseline" },
        { value: "Midline", label: "Midline" },
        { value: "Endline", label: "Endline" },
      ],
    },
  ];

  const handleFilterChange = (appliedFilters) => {
    setFilters(appliedFilters);
  };

  const stripPrefixedSvgAttributes = (root) => {
    const removed = [];
    const hidden = [];
    root.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const prefix = attr.name.includes(":") ? attr.name.split(":")[0] : null;
        if (!prefix || ["xmlns", "xlink", "xml"].includes(prefix)) return;
        removed.push([el, attr.name, attr.value]);
        el.removeAttribute(attr.name);
      });
      const style = getComputedStyle(el);
      const outOfFlow =
        (el instanceof SVGElement && el.tagName.toLowerCase() !== "svg") ||
        style.position === "absolute" ||
        style.position === "fixed";
      if (outOfFlow && (style.opacity === "0" || style.visibility === "hidden")) {
        hidden.push([el, el.style.display]);
        el.style.display = "none";
      }
    });
    return () => {
      removed.forEach(([el, name, value]) => el.setAttribute(name, value));
      hidden.forEach(([el, display]) => (el.style.display = display));
    };
  };

  const fileToDataUrl = async (url) => {
    const blob = await (await fetch(url)).blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const buildLocalFontEmbedCSS = async () => {
    const fontRules = [];
    Array.from(document.styleSheets).forEach((sheet) => {
      if (sheet.href && new URL(sheet.href, window.location.href).origin !== window.location.origin) return;
      let rules;
      try {
        rules = sheet.cssRules;
      } catch {
        return;
      }
      Array.from(rules || []).forEach((rule) => {
        if (rule instanceof CSSFontFaceRule) fontRules.push(rule);
      });
    });
    const inlined = await Promise.all(
      fontRules.map(async (rule) => {
        let css = rule.cssText;
        const base = rule.parentStyleSheet?.href || window.location.href;
        const urls = [...css.matchAll(/url\(["']?([^"')]+)["']?\)/g)]
          .map((match) => match[1])
          .filter((url) => !url.startsWith("data:"));
        for (const url of [...new Set(urls)]) {
          try {
            css = css.split(url).join(await fileToDataUrl(new URL(url, base).href));
          } catch (fontError) {
            console.warn("PDF: could not embed font", url, fontError);
          }
        }
        return css;
      })
    );
    return inlined.join("\n");
  };

  const findSectionContainer = (el) => {
    let current = el;
    while (
      current.children.length === 1 &&
      current.children[0].children &&
      current.children[0].children.length > 1
    ) {
      current = current.children[0];
    }
    return current;
  };

  const waitForStableHeight = async (el, { stableChecks = 4, interval = 120, maxWait = 3000 } = {}) => {
    const start = Date.now();
    let lastHeight = el.offsetHeight;
    let stableCount = 0;
    while (stableCount < stableChecks && Date.now() - start < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, interval));
      const height = el.offsetHeight;
      if (height === lastHeight && height > 0) {
        stableCount++;
      } else {
        stableCount = 0;
        lastHeight = height;
      }
    }
  };

  const waitForDomToSettle = (root, { quietMs = 500, maxWait = 6000 } = {}) => {
    return new Promise((resolve) => {
      let quietTimer;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        observer.disconnect();
        clearTimeout(quietTimer);
        clearTimeout(maxTimer);
        resolve();
      };
      const observer = new MutationObserver(() => {
        clearTimeout(quietTimer);
        quietTimer = setTimeout(finish, quietMs);
      });
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
      quietTimer = setTimeout(finish, quietMs);
      const maxTimer = setTimeout(finish, maxWait);
    });
  };

  const measureFullHeight = (root) => {
    const rootTop = root.getBoundingClientRect().top;
    let maxBottom = root.getBoundingClientRect().height;
    const walk = (node) => {
      for (const child of node.children) {
        const bottom = child.getBoundingClientRect().bottom - rootTop;
        if (bottom > maxBottom) maxBottom = bottom;
        walk(child);
      }
    };
    walk(root);
    return maxBottom;
  };

  const handleDownloadAllPDF = async () => {
    if (isDownloadingPDF) return;
    const originalTab = activeSubTab;
    const LEFT_PAGE = "left-page";
    const stopIfLeftPage = () => {
      if (leftPageRef.current) throw new Error(LEFT_PAGE);
    };
    setIsDownloadingPDF(true);
    const truncateOverrideStyle = document.createElement("style");
    truncateOverrideStyle.textContent = `
      #hh-pdf-capture-root .truncate {
        overflow: visible !important;
        text-overflow: unset !important;
      }
    `;
    document.head.appendChild(truncateOverrideStyle);
    try {
      const pdf = new jsPDF("l", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const contentWidth = pdfWidth - margin * 2;
      let hasContent = false;
 
      const CAPTURE_SCALE = 1.5;
      let fontEmbedCSS;
      try {
        fontEmbedCSS = await buildLocalFontEmbedCSS();
      } catch (fontError) {
        console.warn("Could not embed fonts for the PDF:", fontError);
      }

      for (let i = 0; i < tabs.length; i++) {
        const tab = tabs[i];
        stopIfLeftPage();
        setPdfProgress(`Capturing ${tab.label}... (${i + 1}/${tabs.length})`);
        setActiveSubTab(tab.id);

        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );
        stopIfLeftPage();
        if (tabContentRef.current) {
          for (let attempt = 0; attempt < 5; attempt++) {
            await waitForRequestsIdle(60000);
            stopIfLeftPage();
            await waitForDomToSettle(tabContentRef.current, {
              quietMs: 500,
              maxWait: 8000,
            });
            stopIfLeftPage();
            if (getPendingRequestCount() === 0) break;
          }
          await new Promise((resolve) => setTimeout(resolve, 900));
        }
        stopIfLeftPage();

        const root = tabContentRef.current;
        if (!root) continue;
        const sectionContainer = findSectionContainer(root);
        const rows = Array.from(sectionContainer.children).filter(
          (el) => el.offsetWidth > 0 && el.offsetHeight > 10
        );
        if (rows.length === 0) continue;

        await waitForStableHeight(sectionContainer);
        sectionContainer.scrollIntoView({ block: "start", behavior: "instant" });
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );

        const containerRect = sectionContainer.getBoundingClientRect();
        const captureWidth = Math.ceil(
          Math.max(containerRect.width, sectionContainer.scrollWidth)
        );
        const captureHeight =
          Math.ceil(
            Math.max(
              containerRect.height,
              sectionContainer.scrollHeight,
              measureFullHeight(sectionContainer)
            )
          ) + 15;

        const describeError = (error) =>
          error?.message ||
          (error?.target?.tagName
            ? `${error.type} loading <${error.target.tagName.toLowerCase()}>`
            : String(error));
        const attempts = [
          {
            name: "html-to-image",
            run: () =>
              toCanvas(sectionContainer, {
                pixelRatio: CAPTURE_SCALE,
                backgroundColor: "#ffffff",
                width: captureWidth,
                height: captureHeight,
                fontEmbedCSS,
              }),
          },
          {
            name: "html-to-image without embedded fonts",
            run: () =>
              toCanvas(sectionContainer, {
                pixelRatio: CAPTURE_SCALE,
                backgroundColor: "#ffffff",
                width: captureWidth,
                height: captureHeight,
                skipFonts: true,
              }),
          },
          {
            name: "html2canvas",
            run: () =>
              html2canvas(sectionContainer, {
                scale: CAPTURE_SCALE,
                useCORS: true,
                backgroundColor: "#ffffff",
                width: captureWidth,
                height: captureHeight,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                scrollX: 0,
                scrollY: -window.scrollY,
              }),
          },
        ];
        let canvas;
        const restoreChartAttributes = stripPrefixedSvgAttributes(sectionContainer);
        try {
          for (const attempt of attempts) {
            if (!sectionContainer.isConnected) break;
            try {
              canvas = await attempt.run();
              break;
            } catch (captureError) {
              console.warn(
                `PDF: ${tab.label} could not be captured with ${attempt.name}: ${describeError(captureError)}`
              );
            }
          }
        } finally {
          restoreChartAttributes();
        }
        stopIfLeftPage();
        if (!canvas) {
          console.warn(`PDF: skipped ${tab.label}, every capture method failed.`);
          continue;
        }

        const scaleRatio = canvas.height / captureHeight;
        const rowBoundaries = rows.map((row) => {
          const r = row.getBoundingClientRect();
          return {
            top: (r.top - containerRect.top) * scaleRatio,
            bottom: (r.bottom - containerRect.top) * scaleRatio,
          };
        });

        if (hasContent) pdf.addPage();
        hasContent = true;
        pdf.setFontSize(16);
        pdf.setTextColor(19, 126, 129);
        pdf.text(tab.label, margin, margin + 12);
        let currentY = margin + 32;

        const pxToPt = contentWidth / canvas.width;
        const fullPageHeightPt = pdfHeight - margin * 2;
        let rowStartPx = 0;

        for (let r = 0; r < rowBoundaries.length; r++) {
          const rowBottomPx = rowBoundaries[r].bottom;
          const rowHeightPx = rowBottomPx - rowStartPx;
          if (rowHeightPx <= 0) continue;
          const rowHeightPt = rowHeightPx * pxToPt;

          let remaining = pdfHeight - margin - currentY;
          if (rowHeightPt > remaining && currentY > margin + 32) {
            pdf.addPage();
            currentY = margin;
            remaining = fullPageHeightPt;
          }

          let drawWidthPt = contentWidth;
          let drawHeightPt = rowHeightPt;
          if (rowHeightPt > remaining) {
            const scaleFactor = remaining / rowHeightPt;
            drawHeightPt = remaining;
            drawWidthPt = contentWidth * scaleFactor;
          }

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.ceil(rowHeightPx);
          const sliceContext = sliceCanvas.getContext("2d");
          sliceContext.fillStyle = "#ffffff";
          sliceContext.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          sliceContext
            .drawImage(
              canvas,
              0,
              rowStartPx,
              canvas.width,
              rowHeightPx,
              0,
              0,
              canvas.width,
              rowHeightPx
            );
          const x = margin + (contentWidth - drawWidthPt) / 2;
          pdf.addImage(
            sliceCanvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            x,
            currentY,
            drawWidthPt,
            drawHeightPt,
            undefined,
            "FAST"
          );

          currentY += drawHeightPt;
          rowStartPx = rowBottomPx;
        }
      }

      stopIfLeftPage();
      pdf.save(
        `HH-Dashboard-All-KPIs-${new Date().toISOString().slice(0, 10)}.pdf`
      );
    } catch (error) {
      if (error?.message === LEFT_PAGE || leftPageRef.current) {
        toast.info("PDF download cancelled because you left the dashboard.");
      } else {
        console.error("Error generating full HH dashboard PDF:", error);
        toast.error("Could not generate the PDF. Please try again.");
      }
    } finally {
      document.head.removeChild(truncateOverrideStyle);
      if (!leftPageRef.current) {
        setActiveSubTab(originalTab);
        setIsDownloadingPDF(false);
        setPdfProgress("");
      }
    }
  };

  const tabs = [
    // { id: "general", label: "General" },
    { id: "info-members", label: "Demographics" },
    { id: "education", label: "Education" },
    { id: "employment", label: "Employment" },
    { id: "housing", label: "Housing" },
    { id: "drainage", label: "Drainage" },
    { id: "assets", label: "Assets" },
    { id: "health-status", label: "Health Status" },
    { id: "others", label: "Others" },
  ];

  return (
    <div className="w-full px-4 bg-gray-50 min-h-screen">

      {isDownloadingPDF && (
        <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#137e81] mb-4"></div>
          <p className="text-[#137e81] font-semibold">
            {pdfProgress || "Generating PDF..."}
          </p>
        </div>
      )}
      {/* Filters */}
      <div className="my-4">
        <DashboardSearchFilters
          accordionTitle="Filters"
          defaultExpanded={false}
          fetchOnChange={false}
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          dateFields={["date_of_interview"]}
          onApply={handleFilterChange}
          extraFilters={surveyTypeExtraFilter}
        />
      </div>

      {/* Sub-Tabs */}
      <div className="flex border-b border-gray-200 mb-4 bg-white rounded-lg shadow-sm overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            disabled={isDownloadingPDF}
            className={`px-6 py-3 text-sm font-semibold whitespace-nowrap transition-all duration-200 border-b-4 disabled:cursor-not-allowed disabled:opacity-60 ${
              activeSubTab === tab.id
                ? "text-[#137e81] border-[#137e81] bg-teal-50"
                : "text-gray-600 border-transparent hover:border-[#137e81] hover:text-[#137e81]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Download PDF */}
      <div className="flex justify-end mb-6">
        <button
          type="button"
          onClick={handleDownloadAllPDF}
          disabled={isDownloadingPDF}
          className="flex items-center gap-2 bg-[#137e81] hover:bg-[#0f6366] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          {isDownloadingPDF ? pdfProgress || "Generating PDF..." : "Download PDF"}
        </button>
      </div>

      {/* Tab Content */}
      <div
        ref={tabContentRef}
        id="hh-pdf-capture-root"
        className="bg-white border-2 border-dashed border-teal-700 rounded-xl shadow-lg p-6"
      >
        {/* {activeSubTab === "general" && <GeneralTab filters={filters} />} */}
        {activeSubTab === "info-members" && (
          <InformationMembersTab filters={filters} />
        )}
        {activeSubTab === "education" && <EducationTab filters={filters} />}
        {activeSubTab === "employment" && <EmploymentTab filters={filters} />}
        {activeSubTab === "housing" && <HousingTab filters={filters} />}
        {activeSubTab === "drainage" && <DrainageTab filters={filters} />}
        {activeSubTab === "assets" && (
          <AssetsTab
            filters={filters}
            fetchData={fetchData}
            navigate={navigate}
            dispatch={dispatch}
          />
        )}
        {activeSubTab === "health-status" && <HealthStatusTab filters={filters} />}
        {activeSubTab === "others" && <OthersTab filters={filters} />}
      </div>
    </div>
  );
};

export default HouseHoldDashboard;